import z from "zod";
import type { FundingMilestone } from "@remi/enums/funding-milestone";

const goodleapErrorTypeValues = [
  "CREDENTIALS_NOT_FOUND",
  "AXIOS_ERROR",
  "LOAN_NOT_FOUND",
  "LOAN_MISSING_AT_VENDOR",
  "BID_NOT_FOUND",
  "INVALID_RESPONSE",
  "DOCUMENT_REQUEST_FAILED",
  "UPLOAD_FAILED",
  "INVALID_TERMS",
  "NO_LOAN_OPTIONS",
  "INVALID_PAYMENT_AMOUNT",
  "OFFER_IDS_REQUIRED",
  "INVALID_RESPONSE_EXPECTED_ARRAY",
  "PAYMENT_REPONSE_PARSE_ERROR",
  "MISSING_SALES_PERSON_INFO",
  "INVALID_CHANGE_ORDER_REQUEST",
  "LOAN_AMOUNT_EXCEEDS_MAXIMUM",
  "MILESTONE_TRANSITION_FAILED",
] as const;

const goodleapErrorTypeSchema = z.enum(goodleapErrorTypeValues);
export const GoodleapErrorType = goodleapErrorTypeSchema.enum;
type GoodleapErrorType = z.infer<typeof goodleapErrorTypeSchema>;

type ErrorDetails = {
  sentryDescription: string;
};

export type GoodleapErrors =
  | {
      errorType: typeof GoodleapErrorType.CREDENTIALS_NOT_FOUND;
      data: undefined;
    }
  | {
      errorType: typeof GoodleapErrorType.AXIOS_ERROR;
      data: {
        response?: unknown;
        statusCode?: number;
        externalId?: string;
        bidId?: number;
        loanId?: string;
        endpoint?: string;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.LOAN_NOT_FOUND;
      data: {
        loanId: string;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.LOAN_MISSING_AT_VENDOR;
      data: {
        loanId: string;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.BID_NOT_FOUND;
      data: {
        bidId: number;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.INVALID_RESPONSE;
      data: {
        endpoint?: string;
        statusCode?: number;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.DOCUMENT_REQUEST_FAILED;
      data: {
        loanId?: string;
        externalId?: string;
        reason?: string;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.UPLOAD_FAILED;
      data: {
        loanId?: string;
        externalId?: string;
        fileName?: string;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.INVALID_TERMS;
      data: {
        externalId?: string;
        bidId?: number;
        loanId?: string;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.NO_LOAN_OPTIONS;
      data: {
        externalId: string;
        requestedApr?: number | null;
        requestedTermMonths?: number | null;
        availableOffers: Array<{
          id: string;
          rate: number | string;
          term: number | string;
          termMonths: number;
        }>;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.INVALID_PAYMENT_AMOUNT;
      data: {
        amount: number;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.OFFER_IDS_REQUIRED;
      data: undefined;
    }
  | {
      errorType: typeof GoodleapErrorType.INVALID_RESPONSE_EXPECTED_ARRAY;
      data: {
        endpoint: string;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.PAYMENT_REPONSE_PARSE_ERROR;
      data: {
        parseError: unknown;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.MISSING_SALES_PERSON_INFO;
      data: {
        bidId: number;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.INVALID_CHANGE_ORDER_REQUEST;
      data: {
        loanId: string;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.LOAN_AMOUNT_EXCEEDS_MAXIMUM;
      data: {
        loanId: string;
      };
    }
  | {
      errorType: typeof GoodleapErrorType.MILESTONE_TRANSITION_FAILED;
      data: {
        externalId: string;
        goodleapLoanId: string;
        fundingMilestone: FundingMilestone;
        upstreamError?: string;
      };
    };

export const goodleapErrorDetails: Record<GoodleapErrorType, ErrorDetails> = {
  [GoodleapErrorType.CREDENTIALS_NOT_FOUND]: {
    sentryDescription: "Goodleap credentials not found",
  },
  [GoodleapErrorType.AXIOS_ERROR]: {
    sentryDescription: "Failed to fetch data from Goodleap API",
  },
  [GoodleapErrorType.LOAN_NOT_FOUND]: {
    sentryDescription: "Goodleap loan not found",
  },
  [GoodleapErrorType.LOAN_MISSING_AT_VENDOR]: {
    sentryDescription:
      "GoodLeap has no loan with this ID under Remi's account. Check the loan number on this financing.",
  },
  [GoodleapErrorType.BID_NOT_FOUND]: {
    sentryDescription: "Goodleap bid not found",
  },
  [GoodleapErrorType.INVALID_RESPONSE]: {
    sentryDescription: "Invalid response from Goodleap API",
  },
  [GoodleapErrorType.DOCUMENT_REQUEST_FAILED]: {
    sentryDescription: "Goodleap document request failed",
  },
  [GoodleapErrorType.UPLOAD_FAILED]: {
    sentryDescription: "Goodleap file upload failed",
  },
  [GoodleapErrorType.INVALID_TERMS]: {
    sentryDescription: "Goodleap returned invalid financing terms",
  },
  [GoodleapErrorType.NO_LOAN_OPTIONS]: {
    sentryDescription: "Goodleap returned no loan options",
  },
  [GoodleapErrorType.INVALID_PAYMENT_AMOUNT]: {
    sentryDescription: "Amount must be greater than or equal to 1",
  },
  [GoodleapErrorType.OFFER_IDS_REQUIRED]: {
    sentryDescription: "At least one offer ID is required",
  },
  [GoodleapErrorType.INVALID_RESPONSE_EXPECTED_ARRAY]: {
    sentryDescription: "Invalid response - expected array",
  },
  [GoodleapErrorType.PAYMENT_REPONSE_PARSE_ERROR]: {
    sentryDescription: "Payment response parse error",
  },
  [GoodleapErrorType.MISSING_SALES_PERSON_INFO]: {
    sentryDescription: "Missing sales person info",
  },
  [GoodleapErrorType.INVALID_CHANGE_ORDER_REQUEST]: {
    sentryDescription: "Invalid change order request - a ticket was created for sales success to handle this",
  },
  [GoodleapErrorType.LOAN_AMOUNT_EXCEEDS_MAXIMUM]: {
    sentryDescription:
      "GoodLeap rejected the change order because the amount is above the loan's approved maximum - a ticket was created for sales success to handle this",
  },
  [GoodleapErrorType.MILESTONE_TRANSITION_FAILED]: {
    sentryDescription: "Failed to transition GoodLeap milestone to INSTALL_COMPLETE",
  },
};
